import { AnimatePresence, motion } from "motion/react"
import { shuffle } from "lodash"
import { useEffect, useMemo, useState } from "react"

import { cn } from "~/utils/helpers"
import { Loading } from "~/components/reusable/Loading"

export const WAITING_MESSAGES = [
  "Getting everything ready...",
  "Generating forecasts...",
  "Preparing order suggestions...",
  "Analysing trends....",
  "Checking inventory...",
  "Calculating availability...",
]

interface LoadingOverlayProps {
  styles?: string
  messages?: string | string[]
  height?: string
  randomize?: boolean
  size?: "base" | "lg"
}

const LoadingOverlay = ({
  styles,
  messages,
  height = "h-full",
  randomize = true,
  size = "base",
}: LoadingOverlayProps) => {
  const isRotating = Array.isArray(messages)
  const [messageIndex, setMessageIndex] = useState(0)

  const orderedMessages = useMemo(() => {
    if (!isRotating) return []
    if (!randomize) return messages
    const [first, ...rest] = messages
    return [first!, ...shuffle(rest)]
  }, [isRotating, messages, randomize])

  useEffect(() => {
    if (!isRotating) return
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % orderedMessages.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [isRotating, orderedMessages.length])

  return (
    <div className={cn("mx-auto flex flex-col items-center justify-center p-4", height, styles)}>
      <div className="flex flex-col gap-4 text-center items-center" role="status">
        <Loading size={size} />
        <span className="sr-only">Loading</span>
        {typeof messages === "string" ? (
          <span className="font-medium text-lg animate-pulse">{messages}</span>
        ) : isRotating ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={`message-${messageIndex}`}
              className="font-medium text-lg animate-pulse"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {orderedMessages[messageIndex]}
            </motion.div>
          </AnimatePresence>
        ) : null}
      </div>
    </div>
  )
}

export default LoadingOverlay
